import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
    X, Loader2, Sparkles, FileDown, ClipboardCheck, Plus, Trash2, AlertTriangle,
    Award, Factory,
} from 'lucide-react';

const VIOLET = '#7C6BEF';

const SOURCE_META = {
    master: { label: 'Master', color: '#1F8FB8', bg: 'rgba(31,143,184,0.12)' },
    rubro: { label: 'Rubro', color: '#7C6BEF', bg: 'rgba(124,107,239,0.12)' },
    ai: { label: 'IA', color: '#E08B3C', bg: 'rgba(224,139,60,0.15)' },
};

const WEIGHT_LABEL = { 1: 'Estándar', 2: 'NOM', 3: 'Crítico' };

/**
 * ChecklistPreviewModal
 * Displays a preliminary checklist for an admin based on client's desired
 * certification + rubro. Admin can enrich with AI, add/remove items, and
 * download a PDF for quotation purposes.
 */
export default function ChecklistPreviewModal({ clientId, clientLabel, onClose }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [data, setData] = useState(null);
    const [enriching, setEnriching] = useState(false);
    const [downloading, setDownloading] = useState(false);
    // Local editable sections: { section_title, items[] }
    const [sections, setSections] = useState([]);

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const r = await api.get(`/admin/clients/${clientId}/checklist-preview`);
            setData(r.data);
            const merged = [
                ...(r.data.master_sections || []).map((s) => ({
                    section_title: `Master · ${s.section_title || 'Sección'}`,
                    items: s.items || [],
                })),
                ...(r.data.rubro_sections || []).map((s) => ({
                    section_title: `Rubro · ${s.section_title || 'Área'}`,
                    items: s.items || [],
                })),
            ];
            setSections(merged);
        } catch (e) {
            setError(apiErrorMessage(e, 'No se pudo generar el preview.'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    const totalItems = useMemo(
        () => sections.reduce((n, s) => n + (s.items?.length || 0), 0),
        [sections],
    );

    const canEnrich = !!data?.standard;

    const enrichWithAI = async () => {
        if (!canEnrich) {
            toast.error('El cliente no tiene distintivo deseado configurado.');
            return;
        }
        setEnriching(true);
        try {
            const r = await api.post(`/admin/clients/${clientId}/checklist-preview/enrich`, {});
            const newItems = r.data?.items || [];
            if (newItems.length === 0) {
                toast.info('La IA no sugirió ítems adicionales.');
                return;
            }
            setSections((prev) => {
                const idx = prev.findIndex((s) => s.section_title.startsWith('IA · '));
                const aiSection = {
                    section_title: 'IA · Sugerencias específicas',
                    items: [...(idx >= 0 ? prev[idx].items : []), ...newItems],
                };
                if (idx >= 0) {
                    const clone = [...prev];
                    clone[idx] = aiSection;
                    return clone;
                }
                return [...prev, aiSection];
            });
            toast.success(`IA agregó ${newItems.length} ítem(s)`);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al enriquecer con IA'));
        } finally {
            setEnriching(false);
        }
    };

    const removeItem = (secIdx, itemId) => {
        setSections((prev) => {
            const clone = [...prev];
            clone[secIdx] = {
                ...clone[secIdx],
                items: clone[secIdx].items.filter((it) => it.id !== itemId),
            };
            return clone;
        });
    };

    const addBlankItem = (secIdx) => {
        setSections((prev) => {
            const clone = [...prev];
            const title = clone[secIdx]?.section_title || '';
            // Infer source from section prefix so the badge matches its area
            let source = 'master';
            if (title.startsWith('IA · ')) source = 'ai';
            else if (title.startsWith('Rubro · ')) source = 'rubro';
            const newItem = {
                id: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                question: 'Nueva pregunta (edítala haciendo clic sobre el texto)',
                weight: 2,
                standard_reference: '',
                source,
            };
            clone[secIdx] = { ...clone[secIdx], items: [...clone[secIdx].items, newItem] };
            return clone;
        });
    };

    const updateItem = (secIdx, itemId, patch) => {
        setSections((prev) => {
            const clone = [...prev];
            clone[secIdx] = {
                ...clone[secIdx],
                items: clone[secIdx].items.map((it) => (it.id === itemId ? { ...it, ...patch } : it)),
            };
            return clone;
        });
    };

    const downloadPdf = async () => {
        setDownloading(true);
        try {
            const payload = {
                standard_code: data?.standard?.code || '',
                standard_name: data?.standard?.name || '',
                rubro_name: data?.rubro?.name || '',
                client_label: clientLabel || '',
                sections,
            };
            const r = await api.post(
                `/admin/clients/${clientId}/checklist-preview/pdf`,
                payload,
                { responseType: 'blob' },
            );
            const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `checklist-preview-${clientId}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('PDF generado');
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al generar PDF'));
        } finally {
            setDownloading(false);
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
            onClick={onClose}
            data-testid="checklist-preview-backdrop"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-5xl my-8"
                style={{ background: 'var(--card-bg)', border: `2px solid ${VIOLET}` }}
                data-testid="checklist-preview-modal"
            >
                {/* Header */}
                <div className="flex items-start justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center" style={{ background: `${VIOLET}22`, color: VIOLET }}>
                            <ClipboardCheck className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: VIOLET }}>Preview de cotización</div>
                            <h2 className="font-display text-xl font-black">Checklist preliminar</h2>
                            {clientLabel && <div className="text-xs text-gray-500 mt-0.5">{clientLabel}</div>}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="checklist-preview-close-btn"
                        className="p-2 hover:bg-black/5 dark:hover:bg-white/10"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Meta bar */}
                {!loading && data && (
                    <div className="px-6 py-3 border-b grid grid-cols-1 md:grid-cols-3 gap-3 text-xs" style={{ borderColor: 'var(--border)', background: 'var(--muted-bg, transparent)' }}>
                        <MetaChip
                            icon={Award}
                            label="Distintivo (Mi distintivo)"
                            value={data.standard ? `${data.standard.code} — ${data.standard.name}` : 'Sin definir'}
                            warn={!data.standard}
                        />
                        <MetaChip
                            icon={Factory}
                            label="Rubro / Industria"
                            value={data.rubro ? `${data.rubro.name} (${data.rubro.category || '—'})` : (data.client?.industry || 'Sin rubro')}
                            warn={!data.rubro}
                        />
                        <MetaChip
                            icon={ClipboardCheck}
                            label="Total de ítems"
                            value={String(totalItems)}
                        />
                    </div>
                )}

                {/* Warnings */}
                {!loading && data?.notes?.filter(Boolean).length > 0 && (
                    <div className="px-6 py-2">
                        {data.notes.filter(Boolean).map((n, i) => (
                            <div key={i} className="text-xs text-amber-700 dark:text-amber-500 inline-flex items-center gap-2 mr-4">
                                <AlertTriangle className="w-3.5 h-3.5" /> {n}
                            </div>
                        ))}
                    </div>
                )}

                {/* Body */}
                <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                    {loading && (
                        <div className="py-12 text-center text-gray-500 text-sm">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                            Cargando preview...
                        </div>
                    )}
                    {error && !loading && (
                        <div className="py-12 text-center text-red-500 text-sm">{error}</div>
                    )}
                    {!loading && !error && sections.length === 0 && (
                        <div className="py-12 text-center text-gray-500 text-sm">
                            No hay ítems para mostrar. Verifica que el cliente tenga distintivo deseado y rubro.
                        </div>
                    )}
                    {!loading && sections.map((section, secIdx) => (
                        <div key={`${section.section_title}-${secIdx}`} className="mb-5" data-testid={`preview-section-${secIdx}`}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-xs uppercase tracking-[0.15em] font-black" style={{ color: VIOLET }}>
                                    {section.section_title}
                                    <span className="ml-2 text-gray-400 font-normal">({section.items.length})</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => addBlankItem(secIdx)}
                                    className="text-xs inline-flex items-center gap-1 px-2 py-1 border font-bold hover:bg-black/5 dark:hover:bg-white/10"
                                    style={{ borderColor: 'var(--border)' }}
                                    data-testid={`preview-add-item-${secIdx}`}
                                >
                                    <Plus className="w-3 h-3" /> Añadir
                                </button>
                            </div>
                            <div className="border" style={{ borderColor: 'var(--border)' }}>
                                {section.items.map((it, itIdx) => {
                                    const meta = SOURCE_META[it.source] || SOURCE_META.master;
                                    return (
                                        <div
                                            key={it.id}
                                            className="flex items-start gap-3 p-3 border-b last:border-b-0 text-sm"
                                            style={{ borderColor: 'var(--border)' }}
                                            data-testid={`preview-item-${secIdx}-${itIdx}`}
                                        >
                                            <div className="text-xs text-gray-400 font-mono w-6 shrink-0 mt-1">{itIdx + 1}</div>
                                            <div className="flex-1 min-w-0">
                                                <div
                                                    contentEditable
                                                    suppressContentEditableWarning
                                                    onBlur={(e) => updateItem(secIdx, it.id, { question: e.currentTarget.textContent })}
                                                    className="outline-none focus:bg-yellow-50 dark:focus:bg-yellow-950/20 px-1 -mx-1"
                                                >
                                                    {it.question}
                                                </div>
                                                {it.standard_reference && (
                                                    <div className="text-[11px] text-gray-500 mt-1">{it.standard_reference}</div>
                                                )}
                                            </div>
                                            <div className="shrink-0 flex items-center gap-2">
                                                <select
                                                    value={it.weight}
                                                    onChange={(e) => updateItem(secIdx, it.id, { weight: Number(e.target.value) })}
                                                    className="text-[11px] border px-1 py-0.5 outline-none"
                                                    style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}
                                                    data-testid={`preview-weight-${secIdx}-${itIdx}`}
                                                >
                                                    <option value={1}>1 · Estándar</option>
                                                    <option value={2}>2 · NOM</option>
                                                    <option value={3}>3 · Crítico</option>
                                                </select>
                                                <span
                                                    className="text-[9px] uppercase tracking-wider font-black px-1.5 py-0.5"
                                                    style={{ background: meta.bg, color: meta.color }}
                                                    title={WEIGHT_LABEL[it.weight]}
                                                >
                                                    {meta.label}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(secIdx, it.id)}
                                                    className="p-1 text-gray-400 hover:text-red-600"
                                                    data-testid={`preview-remove-${secIdx}-${itIdx}`}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                                {section.items.length === 0 && (
                                    <div className="p-4 text-xs text-gray-400 italic text-center">Sin ítems en esta sección</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer actions */}
                <div className="px-6 py-4 border-t flex flex-wrap gap-2 justify-between items-center" style={{ borderColor: 'var(--border)' }}>
                    <div className="text-xs text-gray-500">
                        Los ítems se cargan del checklist maestro + el rubro. Puedes enriquecer con IA o editar antes de descargar.
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={enrichWithAI}
                            disabled={enriching || !canEnrich || loading}
                            data-testid="checklist-preview-enrich-btn"
                            className="inline-flex items-center gap-2 px-4 py-2 border-2 font-bold text-sm disabled:opacity-40"
                            style={{ borderColor: '#E08B3C', color: '#E08B3C' }}
                            title={canEnrich ? 'Agregar sugerencias de IA basadas en la norma + rubro' : 'Requiere que el cliente haya definido su distintivo deseado'}
                        >
                            {enriching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                            Enriquecer con IA
                        </button>
                        <button
                            type="button"
                            onClick={downloadPdf}
                            disabled={downloading || loading || totalItems === 0}
                            data-testid="checklist-preview-pdf-btn"
                            className="inline-flex items-center gap-2 px-4 py-2 text-white font-bold text-sm disabled:opacity-40"
                            style={{ background: VIOLET }}
                        >
                            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                            Descargar PDF
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}

function MetaChip({ icon: Icon, label, value, warn }) {
    return (
        <div
            className="flex items-start gap-2 p-2 border"
            style={{
                borderColor: warn ? '#F59E0B' : 'var(--border)',
                background: warn ? 'rgba(245,158,11,0.06)' : 'transparent',
            }}
        >
            <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: warn ? '#F59E0B' : VIOLET }} />
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">{label}</div>
                <div className="text-sm font-bold truncate" title={value}>{value}</div>
            </div>
        </div>
    );
}
